/**
 * useSuppliers.js
 * ---------------
 * React hook that fetches the Suppliers list via get_suppliers.
 *
 * Mirrors useReferenceData's { loading, error } state conventions and
 * adds a refetch() for the create-then-select flow in SupplierPicker.
 *
 * Returns:
 *   suppliers: Array<Supplier>  — empty array while loading or on error
 *   loading:   boolean
 *   error:     Error | null
 *   refetch:   () => void       — bumps an internal version to retrigger fetch
 *
 * Re-fetches when callerEmail changes. No module-level cache; the picker
 * needs fresh data after createSupplier, and refetch() keeps that flow
 * simple.
 */

import { useState, useEffect, useCallback } from 'react';
import { getSuppliers } from '../../features/admin/supplierManagementApi';

export function useSuppliers(callerEmail) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [version, setVersion]     = useState(0);

  useEffect(() => {
    if (!callerEmail) {
      setSuppliers([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getSuppliers(callerEmail)
      .then((list) => {
        if (cancelled) return;
        setSuppliers(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('useSuppliers fetch error:', err);
        setSuppliers([]);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [callerEmail, version]);

  const refetch = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  return { suppliers, loading, error, refetch };
}
